/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import type { Editor } from '@tiptap/react';
import { useCallback, useMemo, useState } from 'react';

import IcoArrowDropDownRounded from 'components/icon/icons/IcoArrowDropDownRounded';
import Icon from '../components/Icon';
import { DEFAULT_FONT_FAMILY } from 'components/editor/grid/reduxStore/defaultTextDefaultValue.ts';
import { useAppSelector } from 'components/editor/grid/reduxStore/Store.ts';
import { selectTableTextDefaultStyles, selectTextDefaultStyles } from 'components/editor/grid/reduxStore/textDefaultStylingSlice.ts';
import { selectContentId } from 'components/editor/grid/reduxStore/documentSettingsSlice';
import { useAllAvailableFonts } from '../../../../../hooks/useAllAvailableFonts';
import PyMuiButton from '../components/generalComponents/PyMuiButton';
import PyMuiMenu from '../components/generalComponents/PyMuiMenu';
import PyMuiMenuItem from '../components/generalComponents/PyMuiMenuItem';
import PyMuiMenuLabel from '../components/generalComponents/PyMuiMenuLabel';
import { useAttributeSync } from '../hooks/useAttributeSync.ts';

const isActive = (editor: Editor | null, fontFamily: string | undefined) => editor?.isActive('textStyle', { fontFamily }) ?? false;

const styles = {
  fontFamilyContainer: css({
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'stretch',
  }),
};

type Props = {
  editor: Editor | null;
  disabled?: boolean;
};

const FontFamily = ({ editor, disabled = false }: Props) => {
  const contentId = useAppSelector(selectContentId);
  const allAvailableFonts = useAllAvailableFonts(contentId);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const textDefaultStyles = useAppSelector(selectTextDefaultStyles);
  const tableDefaultStyles = useAppSelector(selectTableTextDefaultStyles);
  const defaultStyles = useMemo(() => ({ ...textDefaultStyles, ...tableDefaultStyles }), [textDefaultStyles, tableDefaultStyles]);
  const [selectedFontFamily, setSelectedFontFamily] = useAttributeSync(editor, defaultStyles, 'fontFamily', DEFAULT_FONT_FAMILY);

  const selectedLabel = useMemo(() => {
    if (selectedFontFamily === undefined || selectedFontFamily === '') return '';

    const matchedFont = allAvailableFonts.find((font) => font.name.toLowerCase() === selectedFontFamily.toLowerCase().trim());

    if (matchedFont) {
      return matchedFont.name;
    }
    return selectedFontFamily;
  }, [selectedFontFamily, allAvailableFonts]);

  const handleOpenMenu = useCallback<React.MouseEventHandler<HTMLElement>>((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => setAnchorEl(null), []);

  const handleSelectFontFamily = useCallback(
    (fontFamily: string) => {
      editor?.chain().focus()[fontFamily ? 'setFontFamily' : 'unsetFontFamily'](fontFamily).run();

      setSelectedFontFamily(fontFamily || DEFAULT_FONT_FAMILY);
      handleClose();
    },
    [editor, handleClose]
  );

  return (
    <span css={styles.fontFamilyContainer}>
      <PyMuiButton
        width={152}
        testId="font-family-button"
        isMenuOpen={Boolean(anchorEl)}
        disabled={disabled}
        onClickAction={handleOpenMenu}
        isActive={isActive(editor, selectedFontFamily)}
      >
        <PyMuiMenuLabel data-testid="font-family-label" style={{ fontFamily: selectedLabel }}>
          {selectedLabel}
        </PyMuiMenuLabel>
        <Icon>
          <IcoArrowDropDownRounded />
        </Icon>
      </PyMuiButton>

      <PyMuiMenu
        testId="font-family-menu"
        id="select-font-family-menu"
        menuListProps="select-font-family-button"
        anchorEl={anchorEl}
        isMenuOpen={Boolean(anchorEl)}
        handleClose={handleClose}
      >
        {allAvailableFonts.map(({ name: fontFamily }) => (
          <PyMuiMenuItem
            key={fontFamily}
            testId={`font-family-menu-item-${fontFamily.replace(/\s+/g, '-').toLowerCase()}`}
            onClick={() => handleSelectFontFamily(fontFamily)}
            isActive={selectedFontFamily === fontFamily}
            menuItem={fontFamily}
            isFontFamily
            customStyle={{ minWidth: 220 }}
          />
        ))}
      </PyMuiMenu>
    </span>
  );
};

export default FontFamily;
